import { useCallback, useEffect, useRef, useState } from "react";
import { type PlaceFeature, type PlaceGeoJSON, type RelatedPlace, getPlaceTrack } from "../lib/api";
import { PlaceTypeLabel } from "../components/place/PlaceTypeLabel";
import type { LandingPageViewData } from "../components/landing/types";
import { FIT_BOUNDS_PADDING, LANDING_PAGE_FLY_TO_ZOOM } from "../lib/mapConstants";
import { cn } from "../lib/utils";
import { routes } from "../lib/routes";
import { useShare } from "../hooks/useShare";
import { BlobDecoration } from "../components/BlobDecoration";
import { GroupedPlaceCards } from "../components/landing/place-cards";
import { PlaceDetailTabs } from "../components/landing/PlaceDetailTabs";
import { ImageLightbox } from "../components/place/ImageLightbox";
import { ImageGallery } from "../components/place/ImageGallery";
import { PlaceStats } from "../components/place/PlaceStats";
import {
  Map,
  useViewport,
  usePanel,
  AdventureMapPlacesLayer,
  RelatedPlacesLayer,
  RouteTrackLayer,
  SelectedPlaceLayer,
  CompassControl,
  ZoomControls,
} from "../components/map";
import { toDetailLevel, type DetailLevel } from "~/lib/detailLevel";

interface DesktopLandingPageProps {
  landingPage: LandingPageViewData;
  places: PlaceGeoJSON;
  selectedPlace: PlaceFeature | null;
  onPlaceSelect: (placeId: string) => void;
  onPlaceDeselect: () => void;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
}

export default function DesktopLandingPage({
  landingPage,
  places,
  selectedPlace,
  onPlaceSelect,
  onPlaceDeselect,
  onSelectRelatedPlace,
}: DesktopLandingPageProps) {
  const share = useShare();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (landingPage.slug) {
        share({
          url: routes.to.landingPages.bySlug(landingPage.slug),
          title: landingPage.name,
        });
      }
    },
    [share, landingPage.slug, landingPage.name],
  );

  // Scroll to map when place is selected
  useEffect(() => {
    if (!selectedPlace || !mapContainerRef.current) return;
    const top = mapContainerRef.current.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top, behavior: "smooth" });
  }, [selectedPlace]);

  const handlePlaceClick = useCallback(
    (feature: PlaceFeature) => {
      onPlaceSelect(feature.id as string);
    },
    [onPlaceSelect],
  );

  return (
    <div className="nature-minimal min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <header className="relative z-10 px-8 py-6 flex justify-between items-center max-w-7xl mx-auto">
        {landingPage.logo ? (
          <img
            src={landingPage.logo.original}
            alt={`Brand logo for ${landingPage.name}`}
            className="h-10 w-auto object-contain"
          />
        ) : (
          // eslint-disable-next-line react/jsx-no-target-blank -- preserve referrer for analytics
          <a
            href="https://noma.to"
            target="_blank"
            rel="noopener"
            className="text-sm tracking-wide text-primary opacity-50 hover:opacity-100 transition-opacity"
          >
            noma.to
          </a>
        )}
        <a
          href={landingPage.slug ? routes.to.landingPages.bySlug(landingPage.slug) : "#"}
          onClick={handleShare}
          className="text-sm tracking-wide text-primary opacity-50 hover:opacity-100 transition-opacity flex items-center gap-2"
        >
          <span>Share</span>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M4 10l6-6M10 4v6M10 4H4" />
          </svg>
        </a>
      </header>

      {/* Hero Section with Map */}
      <section className="relative px-8 pt-16 pb-4 max-w-7xl mx-auto">
        {/* Decorative blobs */}
        <BlobDecoration
          className="absolute -top-20 right-0 w-80 h-80 opacity-25 translate-x-1/3 text-chart-1"
          variant={1}
        />
        <BlobDecoration className="absolute top-40 right-0 w-48 h-48 opacity-15 text-chart-2" variant={2} />
        <BlobDecoration className="absolute -bottom-32 -left-40 w-80 h-80 opacity-30 text-chart-5" variant={3} />
        <BlobDecoration className="absolute bottom-20 -left-20 w-40 h-40 opacity-20 text-accent" variant={4} />

        {/* Title */}
        <div className="relative z-10 text-center max-w-2xl xl:max-w-3xl mx-auto mb-16">
          <h1 className="text-5xl md:text-6xl font-light leading-tight text-foreground mb-6">{landingPage.name}</h1>
          {landingPage.description && (
            <p className="text-lg leading-relaxed text-secondary">{landingPage.description}</p>
          )}
          {landingPage.curatedBy && (
            <p className="text-sm text-muted-foreground mt-4">Curated by {landingPage.curatedBy.displayName}</p>
          )}
        </div>

        {/* Map */}
        <div ref={mapContainerRef} className="relative max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto">
          <div
            className="relative h-[500px] xl:h-[550px] 2xl:h-[600px] rounded-3xl overflow-hidden"
            style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.08)" }}
          >
            <Map.Canvas bounds={places} padding={FIT_BOUNDS_PADDING.desktop}>
              <DesktopMapContent
                places={places}
                selectedPlace={selectedPlace}
                onPlaceClick={handlePlaceClick}
                onPlaceDeselect={onPlaceDeselect}
                onSelectRelatedPlace={onSelectRelatedPlace}
                onImageClick={(index) => {
                  setLightboxIndex(index);
                  setLightboxOpen(true);
                }}
              />
            </Map.Canvas>
          </div>

          {/* Fullscreen lightbox */}
          {selectedPlace && (
            <ImageLightbox
              images={selectedPlace.properties.images}
              placeName={selectedPlace.properties.name}
              initialIndex={lightboxIndex}
              open={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
            />
          )}
        </div>
      </section>

      {/* Places Sections */}
      <main className="relative max-w-4xl mx-auto px-8 py-10">
        {/* Decorative blobs in content area */}
        <BlobDecoration className="absolute top-10 -right-32 w-56 h-56 opacity-15 text-chart-1" variant={2} />
        <BlobDecoration className="absolute top-1/3 -left-28 w-44 h-44 opacity-20 text-chart-3" variant={3} />
        <BlobDecoration className="absolute bottom-1/4 -right-24 w-36 h-36 opacity-15 text-chart-2" variant={1} />

        {places.features.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-lg">Places will appear here</p>
          </div>
        ) : (
          <GroupedPlaceCards
            places={places.features}
            selectedId={selectedPlace?.id as string | null}
            sectionConfigs={landingPage.config?.sections}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="relative py-20 px-8 overflow-hidden">
        {/* Decorative blobs */}
        <BlobDecoration className="absolute -bottom-16 -right-16 w-72 h-72 opacity-20 text-chart-1" variant={4} />
        <BlobDecoration className="absolute -bottom-12 -left-20 w-48 h-48 opacity-15 text-chart-5" variant={2} />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="text-sm text-muted-foreground mb-6">
            Curated with care by {landingPage.curatedBy?.displayName ?? "your local adventurers"}
          </p>
          {/* eslint-disable-next-line react/jsx-no-target-blank -- preserve referrer for analytics */}
          <a
            href="https://noma.to"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-colors hover:bg-muted border border-input text-primary"
          >
            <span className="text-sm font-medium">Create your own map</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 10l6-6M10 4v6M10 4H4" />
            </svg>
          </a>
        </div>
      </footer>
    </div>
  );
}

/** Inner component that uses Map.* hooks */
interface DesktopMapContentProps {
  places: PlaceGeoJSON;
  selectedPlace: PlaceFeature | null;
  onPlaceClick: (feature: PlaceFeature) => void;
  onPlaceDeselect: () => void;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
  onImageClick: (index: number) => void;
}

function DesktopMapContent({
  places,
  selectedPlace,
  onPlaceClick,
  onPlaceDeselect,
  onSelectRelatedPlace,
  onImageClick,
}: DesktopMapContentProps) {
  const { flyTo, isReady } = useViewport();
  const { position } = usePanel();
  const details = toDetailLevel(position);

  // Fly to selected place when it changes (includes related places in viewport if any)
  useEffect(() => {
    if (!selectedPlace || !isReady) return;
    const center = selectedPlace.geometry.coordinates as [number, number];
    const fitWith =
      selectedPlace.properties.relatedPlaces?.map((p) => [p.longitude, p.latitude] as [number, number]) ?? [];
    flyTo(center, { zoom: LANDING_PAGE_FLY_TO_ZOOM, fitWith });
  }, [selectedPlace, isReady, flyTo]);

  return (
    <>
      {/* Map layers (order = z-index: first = bottom) */}
      <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />
      <AdventureMapPlacesLayer
        places={places}
        excludeIds={selectedPlace?.properties.relatedPlaces?.map((p) => p.id)}
        onClick={onPlaceClick}
      />
      <RelatedPlacesLayer
        relatedPlaces={selectedPlace?.properties.relatedPlaces ?? []}
        onClick={onSelectRelatedPlace}
      />
      <SelectedPlaceLayer place={selectedPlace} />

      {/* Map controls */}
      <Map.Controls>
        <CompassControl />
        <ZoomControls />
      </Map.Controls>

      {/* Selected place panel */}
      {selectedPlace && (
        <Map.Panel>
          <PlacePanelContent
            place={selectedPlace}
            details={details}
            onClose={onPlaceDeselect}
            onSelectRelatedPlace={onSelectRelatedPlace}
            onImageClick={onImageClick}
          />
        </Map.Panel>
      )}
    </>
  );
}

/** Panel content for selected place - no positioning wrapper, Map.Panel handles that */
interface PlacePanelContentProps {
  place: PlaceFeature;
  details?: DetailLevel;
  onClose: () => void;
  onSelectRelatedPlace?: (place: RelatedPlace) => void;
  onImageClick: (index: number) => void;
}

function PlacePanelContent({
  place,
  details = "full",
  onClose,
  onSelectRelatedPlace,
  onImageClick,
}: PlacePanelContentProps) {
  const { properties } = place;
  const { images, name, type, description, links, email, phone, pricing } = properties;
  const showGallery = images.length > 0 && details !== "minimal";

  return (
    <>
      {showGallery && (
        <ImageGallery
          images={images}
          placeName={name}
          onClick={onImageClick}
          className={cn({
            "h-36": details === "compact",
            "h-44": details === "full",
          })}
        />
      )}

      <div
        className={cn("px-5 pb-5", {
          "pt-5": showGallery,
          "pt-3": !showGallery && details !== "minimal",
          "pt-1": details === "minimal",
        })}
      >
        {/* Header */}
        <header>
          <div className="flex justify-between items-start gap-4">
            <div className="flex-1 min-w-0">
              <PlaceTypeLabel type={type} pricing={pricing} />
              <h3 className="text-2xl font-medium leading-tight mt-1">{name}</h3>
            </div>

            <button
              onClick={onClose}
              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors text-muted-foreground hover:bg-muted cursor-pointer"
              aria-label="Close place details"
            >
              <svg width="14" height="14" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 2l8 8M10 2L2 10" />
              </svg>
            </button>
          </div>

          <PlaceStats properties={properties} className="text-muted-foreground mt-2" />
        </header>

        <PlaceDetailTabs
          placeId={place.id as string}
          type={type}
          description={description}
          links={links}
          email={email}
          phone={phone}
          pricing={pricing}
          relatedPlaces={properties.relatedPlaces}
          onSelectRelatedPlace={onSelectRelatedPlace}
          className="mt-3"
        />
      </div>
    </>
  );
}
